import React, { useState } from 'react';
import "./StaffingBentoGrid.css";
import { BentoGridModal, CardData } from '../BentoGridModal/BentoGridModal';
import { ChevronRight } from 'lucide-react';
import PayrollAnimation from '../RecruitingBentoGrid/RecruitingBentoComponents/PayrollAnimation';
import SpeedInteractive from '../RecruitingBentoGrid/RecruitingBentoComponents/SpeedInteractive';
import { CompaniesCarousel } from '../RecruitingBentoGrid/RecruitingBentoComponents/CompaniesCarousel';
import TimeInteractive from '../RecruitingBentoGrid/RecruitingBentoComponents/TimeInteractive';
import MoneyInteractive from '../RecruitingBentoGrid/RecruitingBentoComponents/MoneyInteractive';
import ContractAnimation from '../RecruitingBentoGrid/RecruitingBentoComponents/ContractAnimation';


const SF_CARDS: readonly CardData[] = [
    {
        title: 'Run Volume Shifts With Zero Chaos.',
        subtitle: 'Fill 50 warehouse shifts as easily as 5 — automated matching, reminders, and approvals included.',
        buttonOne: 'Explore Operations ›',
        buttonTwo: 'Book a demo',
        points: [
            'Bulk shift creation and worker assignment in seconds',
            'Filter your worker pool by skill, certification, and location',
            'Automated shift reminders cut no-shows by 40%',
            'Digital timesheets eliminate payroll disputes',
        ],
        insideComponent: null,
    },
    {
        title: 'Know Your Workers Before They Show Up.',
        subtitle: 'Reliability scores, certifications, and shift history — all tracked so you always send your best.',
        buttonOne: 'Learn More ›',
        buttonTwo: 'Book a demo',
        points: [
            'Post-shift reliability ratings build a real performance record',
            'Skills and certifications logged and searchable centrally',
            'Automatic hold on workers below your reliability threshold',
            'Client-specific worker preferences and blocklists',
        ],
        insideComponent: null,
    },
    {
        title: 'Quote to Contract in Minutes.',
        subtitle: 'From inbound lead to signed staffing agreement — faster than any competitor can respond.',
        buttonOne: 'Explore Sales ›',
        buttonTwo: 'Book a demo',
        points: [
            'Templated proposals for warehouse, logistics, and food service',
            'Client billing and contract management in one place',
            'Real-time pipeline dashboard for your whole team',
            'Repeat client insights and upsell opportunity alerts',
        ],
        insideComponent: null,
    },
    {
        title: 'Payroll. Compliance. Zero Surprises.',
        subtitle: 'Timesheets flow to payroll automatically — no exports, no disputes, no missing hours.',
        buttonOne: 'See How It Works ›',
        buttonTwo: 'Book a demo',
        points: [
            'Auto-generated timesheets after every shift',
            'Manager approval workflows built in',
            'Direct payroll integration — no manual exports',
            'Full audit trail per worker, per shift',
        ],
        insideComponent: null,
    },
    {
        title: 'Your Entire Staffing Operation. One Platform.',
        subtitle: 'Sales, scheduling, and payroll — all connected, so your team focuses on growth, not coordination.',
        buttonOne: 'See the Platform ›',
        buttonTwo: 'Book a demo',
        points: [
            'Sales pipeline → shift scheduling → payroll in one connected system',
            'No more spreadsheets, missed shifts, or manual follow-up',
            '1,500+ staffing agencies scaled on Worky',
            'Over $250M in payroll powered',
        ],
        insideComponent: null,
    },
];

const StaffingBentoGrid = (): React.ReactElement => {
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [activeCard, setActiveCard] = useState<CardData>(SF_CARDS[0]);

    const handleOpenModal = (card: CardData): void => {
        setActiveCard(card);
        setModalOpen(true);
    };

    const handleClose = (): void => {
        setModalOpen(false);
    };

    return (
        <>
            <div className="sf-wrapper">
                <div className="sf-grid">

                    {/* Row 1: three top cards */}
                    <div className="bento-card sf-card-1">
                        <div className="bento-card-image-container"><img src='./bento-images/users.jpg' style={{transform:'scale(0.9)', marginTop:'-20px', marginRight:'-60px'}}/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{SF_CARDS[0].title}</div>
                                <div className="bento-card-subtitle">{SF_CARDS[0].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    <div className="bento-card sf-card-2">
                        <div className="bento-card-image-container"><img src='./bento-images/guards.jpeg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title" >{SF_CARDS[1].title}</div>
                                <div className="bento-card-subtitle" >{SF_CARDS[1].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Row 1: Sales */}
                    <div className="bento-card sf-card-3">
                        <div className="bento-card-image-container"><ContractAnimation /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{SF_CARDS[2].title}</div>
                                <div className="bento-card-subtitle">{SF_CARDS[2].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Rows 2–3: tall left (payroll / compliance) */}
                    <div className="bento-card sf-card-4">
                        <div className="bento-card-image-container" style={{height:'calc(100% - 100px)',transform:'scale(1.8)',display: 'flex', alignItems: 'center', justifyContent: 'center'}}><PayrollAnimation /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{SF_CARDS[3].title}</div>
                                <div className="bento-card-subtitle">{SF_CARDS[3].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}
                        </div>
                    </div>

                    {/* Rows 2–3: large center (platform hero) */}
                    <div className="bento-card sf-card-5">
                        <div className="bento-card-image-container"><img src='./bento-images/laptopholding.jpg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">{SF_CARDS[4].title}</div>
                                <div className="bento-card-subtitle">{SF_CARDS[4].subtitle}</div>
                            </div>
                            {/* <button className="bento-card-button"><ChevronRight size={20} /></button> */}   
                        </div>
                    </div>

                    {/* Row 2 right: accent stat */}
                    <div className="bento-card sf-card-6">
                        <div className="bento-card-image-container"><img src='./bento-images/warehouse.jpeg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title" >40%</div>
                                <div className="bento-card-subtitle" >fewer no-shows after switching to Worky</div>
                            </div>
                        </div>
                    </div>

                    {/* Row 3 right: accent stat */}
                    <div className="bento-card sf-card-7">
                        <div className="bento-card-image-container"><SpeedInteractive /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">3x</div>
                                <div className="bento-card-subtitle">faster placements vs. manual scheduling</div>
                            </div>
                        </div>
                    </div>

                    {/* Row 4: four bottom feature cards */}
                    <div className="bento-card sf-card-8">
                        <div className="bento-card-image-container" style={{height:'calc(100% - 100px)',transform:'scale(1.5)', marginTop:'30px', marginRight:'30px'}}><MoneyInteractive /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">Payroll Integration</div>
                                <div className="bento-card-subtitle">Timesheets flow directly to payroll — no exports, no errors</div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card sf-card-9">
                        <div className="bento-card-image-container"><img src='./bento-images/healthcare/multiple-facilities.jpg'/></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">Multi-Site Management</div>
                                <div className="bento-card-subtitle">Run dozens of client sites from one unified dashboard</div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card sf-card-10">
                        <div className="bento-card-image-container"><CompaniesCarousel /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">1,500+ Agencies</div>
                                <div className="bento-card-subtitle">trust Worky to run their day-to-day operations</div>
                            </div>
                        </div>
                    </div>

                    <div className="bento-card sf-card-11">
                        <div className="bento-card-image-container"><TimeInteractive /></div>
                        <div className="bento-card-text-container">
                            <div className="bento-card-text">
                                <div className="bento-card-title">24-hr Onboarding</div>
                                <div className="bento-card-subtitle">New workers fully onboarded and shift-ready in under a day</div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <BentoGridModal open={modalOpen} onClose={handleClose} cardData={activeCard} />
        </>
    );
};

export { StaffingBentoGrid };
